import { Handle, Position } from "@xyflow/react";
import { Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { useArchViz } from "../ArchVizContext";
import { RiskBadge } from "./RiskBadge";

export interface SystemBoundaryNodeData {
  label: string;
  description?: string;
  containerCount: number;
  clickable?: boolean;
  hero?: boolean;
  aggregatedRiskCount?: number;
  containersWithRiskCount?: number;
  [key: string]: unknown;
}

const MAX_SUBTITLE_LEN = 80;

// Mirrors the SystemNode subtitle trimmer — first sentence preferred, then
// truncated at a word boundary with ellipsis. SystemBoundaryNode is the
// v0.2 fallback centerpiece and has no curated `headline` field.
function trimSubtitle(description: string | undefined): string {
  const desc = description?.trim();
  if (!desc) return "";
  const firstSentence = desc.match(/^(.+?[.!?])(?:\s|$)/s);
  let candidate = firstSentence?.[1]?.trim() ?? desc;
  if (candidate.length <= MAX_SUBTITLE_LEN) return candidate;
  const sliced = candidate.slice(0, MAX_SUBTITLE_LEN);
  const lastSpace = sliced.lastIndexOf(" ");
  candidate = lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced;
  return `${candidate.replace(/[\s.,;:!?-]+$/, "")}…`;
}

export function SystemBoundaryNode({
  data,
  selected,
}: {
  data: SystemBoundaryNodeData;
  selected?: boolean;
}) {
  const { state, dispatch } = useArchViz();
  const hero = data.hero ?? false;
  const aggregatedRisk = data.aggregatedRiskCount ?? 0;
  const riskContainerCount = data.containersWithRiskCount ?? 0;
  const subtitle = trimSubtitle(data.description);
  return (
    <div
      className={cn(
        "relative rounded-2xl border-2 border-primary/60 bg-primary text-primary-foreground transition-all text-center",
        hero ? "px-10 py-6 shadow-lg min-w-[360px]" : "px-8 py-5 shadow-lg min-w-[280px]",
        data.clickable && "cursor-pointer hover:border-primary",
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)]",
      )}
    >
      {state.showRisks && hero && aggregatedRisk > 0 && (
        <RiskBadge
          count={aggregatedRisk}
          className="-top-3 -right-3 left-auto"
          title={`${aggregatedRisk} risk${aggregatedRisk === 1 ? "" : "s"} across ${riskContainerCount} container${riskContainerCount === 1 ? "" : "s"} — click to view`}
          onClick={() => dispatch({ type: "OPEN_RISKS_SHEET" })}
        />
      )}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right"
        className="!bg-primary-foreground/40"
      />
      <div className="flex items-center justify-center gap-2 mb-2">
        <Layers className={cn("text-primary-foreground", hero ? "h-8 w-8" : "h-7 w-7")} />
      </div>
      <div
        className={cn(
          "text-primary-foreground leading-none tracking-tight",
          hero ? "text-2xl" : "text-xl",
        )}
      >
        {data.label}
      </div>
      {subtitle && (
        <p
          className={cn(
            "mt-2 text-primary-foreground/85 leading-snug line-clamp-2",
            hero ? "text-base" : "text-sm",
          )}
        >
          {subtitle}
        </p>
      )}
      <div className="mt-3 text-[10px] uppercase tracking-eyebrow text-primary-foreground/60">
        {data.containerCount} container{data.containerCount === 1 ? "" : "s"}
      </div>
      <Handle
        type="source"
        position={Position.Top}
        id="top-src"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-src"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-src"
        className="!bg-primary-foreground/40"
      />
    </div>
  );
}
